/**
 * Push-notification capability (src/platform/).
 *
 * Contract: every capability has a working web implementation; native
 * branches (via isNativePlatform()) MUST keep the web fallback. Web push
 * registration (service worker machinery) is explicitly deferred by epic
 * #993 — on web this module only surfaces permission state. On the native
 * shells the Capacitor PushNotifications plugin registers with APNs (iOS)
 * / FCM (Android) and the token is persisted via POST /push/register; the
 * plugin is loaded dynamically so no Capacitor code enters the web bundle.
 */

import type { PluginListenerHandle } from '@capacitor/core'

import { api } from '@/api/client'

import { getPlatform, isNativePlatform } from './detection'

export type PushPermission = 'granted' | 'denied' | 'prompt' | 'unsupported'

export interface PushRegistrationResult {
  supported: boolean
  registered: boolean
  reason?: string
}

function toPushPermission(state: NotificationPermission): PushPermission {
  return state === 'default' ? 'prompt' : state
}

/** Current notification permission, or 'unsupported' when the API is absent. */
export function getPushPermission(): PushPermission {
  if (typeof Notification === 'undefined') return 'unsupported'
  return toPushPermission(Notification.permission)
}

/** Prompt the user for notification permission (web Notification API). */
export async function requestPushPermission(): Promise<PushPermission> {
  if (typeof Notification === 'undefined') return 'unsupported'
  return toPushPermission(await Notification.requestPermission())
}

// How long to wait for APNs/FCM to hand back a device token after register().
const REGISTRATION_TIMEOUT_MS = 15_000

// Per-install marker that we have already shown the OS permission prompt once.
// checkPermissions() is unreliable on some Android OEM skins — it can keep
// reporting 'prompt' even after the user tapped "Don't allow", which re-fires
// the dialog on every launch. This persisted flag makes the prompt strictly
// once-per-install; a later contextual re-request is the user's own action.
const PUSH_PROMPTED_KEY = 'maxflow.push.prompted'

function hasPromptedForPush(): boolean {
  try {
    return globalThis.localStorage?.getItem(PUSH_PROMPTED_KEY) === '1'
  } catch {
    return false
  }
}

function markPromptedForPush(): void {
  try {
    globalThis.localStorage?.setItem(PUSH_PROMPTED_KEY, '1')
  } catch {
    // localStorage unavailable (private mode / no storage) — best-effort only.
  }
}

/** APNs endpoint this build's tokens belong to: sandbox for dev builds, production for release. */
function apnsEnvironment(): 'sandbox' | 'production' {
  const configured = import.meta.env.VITE_APNS_ENVIRONMENT
  if (configured === 'sandbox' || configured === 'production') return configured
  return import.meta.env.MODE === 'production' ? 'production' : 'sandbox'
}

async function registerNative(): Promise<PushRegistrationResult> {
  const { PushNotifications } = await import('@capacitor/push-notifications')

  let permission = await PushNotifications.checkPermissions()
  if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
    // Only ever prompt once per install. If we've asked before and still don't
    // have a grant, respect that prior decision instead of re-prompting.
    if (hasPromptedForPush()) {
      return { supported: true, registered: false, reason: 'Push permission previously declined' }
    }
    markPromptedForPush()
    permission = await PushNotifications.requestPermissions()
  }
  if (permission.receive !== 'granted') {
    return { supported: true, registered: false, reason: 'Push permission denied' }
  }

  // Keep the handle promises (a listener can fire before its handle promise
  // resolves) and remove only these two listeners afterwards — wiping the
  // plugin with removeAllListeners() would also detach any foreground
  // pushNotificationReceived / pushNotificationActionPerformed handlers.
  const handles: Promise<PluginListenerHandle>[] = []
  const token = await new Promise<string | null>((resolve) => {
    const timer = setTimeout(() => resolve(null), REGISTRATION_TIMEOUT_MS)
    handles.push(
      PushNotifications.addListener('registration', (t) => {
        clearTimeout(timer)
        resolve(t.value)
      })
    )
    handles.push(
      PushNotifications.addListener('registrationError', () => {
        clearTimeout(timer)
        resolve(null)
      })
    )
    void PushNotifications.register()
  })
  await Promise.all(handles.map(async (handle) => (await handle).remove()))

  if (!token) {
    return {
      supported: true,
      registered: false,
      reason: 'Device token registration failed or timed out',
    }
  }

  const platform = getPlatform()
  await api.post('/push/register', {
    device_token: token,
    platform,
    ...(platform === 'ios' && { apns_environment: apnsEnvironment() }),
  })
  return { supported: true, registered: true }
}

/**
 * Register this device for push delivery. Native shells register with
 * APNs/FCM and persist the token server-side; on web this is unsupported
 * (the service-worker web push path is deferred by #993) and the existing
 * in-app Notification/SSE channel remains the delivery route.
 */
export async function registerForPush(): Promise<PushRegistrationResult> {
  if (!isNativePlatform()) {
    return {
      supported: false,
      registered: false,
      reason: 'Push registration requires the native shell; web uses in-app notifications/SSE.',
    }
  }
  try {
    return await registerNative()
  } catch (err) {
    return {
      supported: true,
      registered: false,
      reason: err instanceof Error ? err.message : 'Push registration failed',
    }
  }
}
